import {
  Atom,
  Binary,
  BookOpenText,
  Brain,
  Cpu,
  Dna,
  FlaskConical,
  Globe2,
  Landmark,
  Languages,
  LetterText,
  Library,
  MessageCircle,
  Microscope,
  Palette,
  Shapes,
  Sigma,
  Telescope,
  Trophy,
} from "lucide-react";
import type { ComponentType, CSSProperties } from "react";
import { resolveOlympiadArea } from "../domain/olympiadAreas";
import type { Olympiad } from "../domain/types";

const appearanceByArea: Record<
  string,
  { icon: ComponentType<{ size?: number; "aria-hidden"?: boolean | "true" | "false" }>; color: string; background: string }
> = {
  Astronomia: { icon: Telescope, color: "#5b3fc7", background: "#eeeafd" },
  Arte: { icon: Palette, color: "#bd2d75", background: "#fbe8f2" },
  Biologia: { icon: Dna, color: "#13765d", background: "#e4f5ee" },
  Ciências: { icon: Microscope, color: "#08749a", background: "#e3f4f9" },
  Filosofia: { icon: Brain, color: "#6b46a1", background: "#f0eafb" },
  Física: { icon: Atom, color: "#315bb5", background: "#e8eefc" },
  Geografia: { icon: Globe2, color: "#0b7a75", background: "#e2f4f2" },
  Geometria: { icon: Shapes, color: "#087c65", background: "#e3f5ef" },
  História: { icon: Landmark, color: "#9a5725", background: "#f8ede3" },
  Literatura: { icon: Library, color: "#81449a", background: "#f3eafb" },
  "Língua Espanhola": { icon: MessageCircle, color: "#c25b0b", background: "#fff0df" },
  "Língua Inglesa": { icon: Languages, color: "#a63e66", background: "#fae8ef" },
  "Língua Portuguesa": { icon: LetterText, color: "#b7432b", background: "#fbe9e4" },
  Lógica: { icon: Binary, color: "#55751b", background: "#eef5df" },
  Matemática: { icon: Sigma, color: "#1d5ba8", background: "#e6effb" },
  Química: { icon: FlaskConical, color: "#b93653", background: "#fbe7ec" },
  Tecnologia: { icon: Cpu, color: "#4e50b8", background: "#eaebfb" },
  Outras: { icon: BookOpenText, color: "#596579", background: "#edf0f4" },
};

export function OlympiadAreaIcon({
  olympiad,
  size = "default",
}: {
  olympiad: Pick<Olympiad, "acronym" | "area">;
  size?: "default" | "large" | "hero";
}) {
  const area = resolveOlympiadArea(olympiad);
  const appearance = appearanceByArea[area] ?? {
    icon: Trophy,
    color: "#1c4793",
    background: "#e8eef9",
  };
  const Icon = appearance.icon;
  const iconSize = size === "hero" ? 36 : size === "large" ? 25 : 21;

  return (
    <span
      className={`acronym-tile area-icon ${size === "default" ? "" : size}`.trim()}
      role="img"
      aria-label={`Área: ${area}`}
      title={area}
      style={
        {
          "--area-icon-color": appearance.color,
          "--area-icon-background": appearance.background,
        } as CSSProperties
      }
    >
      <Icon size={iconSize} aria-hidden="true" />
    </span>
  );
}
